import * as React from "react";
import { FormattedMessage } from "react-intl";

import Checkbox from "@material-ui/core/Checkbox";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";

import Info from "@material-ui/icons/Info";
import Tooltip from "@material-ui/core/Tooltip";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import { FieldArray } from "formik";
import TextField from "@material-ui/core/TextField";
import Card from "@material-ui/core/Card/Card";
import CardContent from "@material-ui/core/CardContent/CardContent";
import Button from "@material-ui/core/Button";

import { AccountType } from "../../../api";
import { CurrencyTextField, EnhancedTextField, InlineError } from "../../atoms";
import { InlineDatePicker } from "../../atoms/DatePicker";
import { Couple } from "../../molecules";
import CoachPriceTable from "../../molecules/CoachPriceTable";
import AddCoachPrice from "../../organisms/AddCoachPrice";
import AddCoupleForm from "../CoupleAddForm";
import { mapFormikToMuiProps } from "../form-utils";
import { post } from "../../../config";

interface State {
  mentor: string;
  mentee: string;
}

interface Props {
  mode: any;
  companies: any;
  handleChange: any;
  values: any;
  errors: any;
  touched: any;
  classes: any;
}

class Chunk extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      mentor: "",
      mentee: "",
    };
  }
  render() {
    const {
      mode,
      companies,
      values: { coachPrices },
    } = this.props;

    const isCreate = mode === "create";

    const mapper = mapFormikToMuiProps(this.props as any);

    const processes = this.props.values.processes.map((process) => ({
      student: process.student.email || process.student,
      teacher: process.teacher.email || process.teacher,
    }));

    const coachesWithIndividualPrice = coachPrices.map(({ email }) => email);

    const coachesEligibleForIndividualPrice = processes.map(({ teacher }) => teacher).filter((coach) => coachesWithIndividualPrice.indexOf(coach) === -1);
    const theme = createMuiTheme({
      overrides: {
        MuiTooltip: {
          tooltip: {
            backgroundColor: "black",
            maxWidth: 180,
          },
        },
      },
    });

    const handleChange = (value, param) => {
      switch (param) {
        case "mentor":
          this.setState((s) => ({
            mentor: value,
          }));
          return;
        case "mentee":
          this.setState((s) => ({
            mentee: value,
          }));
          return;
      }
    };

    const saveMentorAndMentee = async () => {
      const { mentor, mentee } = this.state;
      if (mentor || mentee) {
        try {
          await post(`/mentoraddmentee`, {
            mentor: this.state.mentor,
            mentee: this.state.mentee,
          });
        } catch (e) {
          console.error(e);
        }
      }
    };

    return (
      <Grid container spacing={24} direction={"column"}>
        {isCreate && (
          <Grid item xs={12}>
            <EnhancedTextField {...mapper("companyId")} autoFocus fullWidth select InputLabelProps={{ shrink: true }}>
              {companies.map(({ name, id }: any) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </EnhancedTextField>
          </Grid>
        )}

        <Grid item>
          <EnhancedTextField {...mapper("name")} fullWidth label={"title"} />
        </Grid>

        {isCreate && (
          <>
            <Grid item>
              <InlineDatePicker {...mapper("start")} fullWidth format={"DD/MM/YYYY"} />
            </Grid>
            <Grid item>
              <InlineDatePicker {...mapper("end")} fullWidth format={"DD/MM/YYYY"} />
            </Grid>
          </>
        )}

        <Grid item>
          <EnhancedTextField {...mapper("sessions")} type={"number"} fullWidth />
        </Grid>
        <Grid item>
          <CurrencyTextField {...mapper("companyPrice")} fullWidth />
        </Grid>
        <Grid item>
          <CurrencyTextField {...mapper("coachPrice")} fullWidth />
        </Grid>
        <Grid item>
          <Grid container xs={12} alignItems={"center"}>
            <FormControlLabel
              control={<Checkbox checked={this.props.values.automaticMatch} onChange={this.props.handleChange} id={"automaticMatch"} />}
              label={<FormattedMessage id={"generic.autoMatching"} />}
            />
            <MuiThemeProvider theme={theme}>
              <Tooltip title={<FormattedMessage id={"automaching.text"} />} placement="top" classes={this.props.classes}>
                <Info />
              </Tooltip>
            </MuiThemeProvider>
          </Grid>
        </Grid>
        {this.props.values.automaticMatch ? (
          <Grid container justify={"space-between"} xs={12} spacing={8}>
            <Grid item xs={6} md={6}>
              <Card>
                <CardContent>
                  <TextField type="text" value={this.state.mentor} onChange={(e) => handleChange(e.currentTarget.value, "mentor")} label="Mentor" fullWidth />
                  <Button style={{ textTransform: "capitalize" }} color={"primary"} onClick={saveMentorAndMentee}>
                    <FormattedMessage id="buttonActions.submit" />
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} md={6}>
              <Card>
                <CardContent>
                  <TextField type="text" value={this.state.mentee} onChange={(e) => handleChange(e.currentTarget.value, "mentee")} label="Mentee" fullWidth />
                  <Button style={{ textTransform: "capitalize" }} color={"primary"} onClick={saveMentorAndMentee}>
                    <FormattedMessage id="buttonActions.submit" />
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ) : (
          <FieldArray
            name={"processes"}
            render={({ push, remove, replace }) => (
              <>
                <Grid item xs={12}>
                  <AddCoupleForm
                    teacherType={AccountType.Mentor}
                    studentType={AccountType.Mentee}
                    programId={this.props.values.name}
                    onSubmit={push}
                    automaticMatch={this.props.values.automaticMatch}
                  />
                </Grid>
                {processes.map(({ teacher, student }, i) => (
                  <Grid item key={student + teacher + i}>
                    <Couple
                      student={student}
                      teacher={teacher}
                      platform={"coaching"}
                      programId={this.props.values.name}
                      onRemove={() => remove(i)}
                      onSave={(couple) => replace(i, { ...this.props.values.processes[i], ...couple })}
                      automaticMatch={this.props.values.automaticMatch}
                    />
                  </Grid>
                ))}
              </>
            )}
          />
        )}
        {this.props.errors.processes && this.props.touched.processes && (
          <Grid item>
            <InlineError i18nErrorId={"validation." + this.props.errors.processes} />
          </Grid>
        )}

        <FieldArray
          name={"coachPrices"}
          render={({ push, remove }) => (
            <>
              <Grid item>
                <AddCoachPrice onAdd={push} coaches={coachesEligibleForIndividualPrice} />
              </Grid>
              <Grid item>
                <CoachPriceTable onDelete={remove} coachPrices={coachPrices} />
              </Grid>
            </>
          )}
        />

        {isCreate && (
          <>
            <Grid item>
              <FormControlLabel
                control={<Checkbox checked={this.props.values.finalEvaluation} onChange={this.props.handleChange} id={"finalEvaluation"} />}
                label={<FormattedMessage id={"labels.finalEvaluation"} />}
              />
            </Grid>
            <Grid item>
              <FormControl>
                <FormLabel>Tripartita</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={this.props.values.tripartitaInitial} onChange={this.props.handleChange} id={"tripartitaInitial"} />}
                    label={<FormattedMessage id={"labels.tripartitaInitial"} />}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={this.props.values.tripartitaIntermediate} onChange={this.props.handleChange} id={"tripartitaIntermediate"} />}
                    label={<FormattedMessage id={"labels.tripartitaIntermediate"} />}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={this.props.values.tripartitaFinal} onChange={this.props.handleChange} id={"tripartitaFinal"} />}
                    label={<FormattedMessage id={"labels.tripartitaFinal"} />}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
          </>
        )}
      </Grid>
    );
  }
}

export default Chunk;



// WEBPACK FOOTER //
// ./src/components/forms/chunks/CoachingProgramChunk.tsx